import {
  Component,
  Input,
  Output,
  EventEmitter,
  inject,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { TooltipModule } from 'primeng/tooltip';
import {
  LucideAngularModule,
  Info,
  ChevronsUp,
  ChevronsDown,
} from 'lucide-angular';
import { Router } from '@angular/router';
import { ChartDoughnutComponent } from '../chart-doughnut/chart-doughnut.component';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
// import { translateElementType } from '../../utils/element-type-translation';
import { ChartSizeService } from '../../services/chart-size.service';
import { CurrentUserService } from '../../../shared/services/current-user.service';
import {
  TripImpact,
  ChartData,
  LegendCategory,
  ChartLogoPosition,
  ChartLogoSource,
  ImpactReportCompany,
  buildChartLogoSources,
} from '../../models';
import { CompanyClient } from 'src/app/trip-calculator/models/companyClients';
import { ImpactCalculator } from '../../utils/impact-calculator';
import { CategoryStateManager } from '../../utils/category-state-manager';
import { ImpactDataService } from '../../services/impact-data.service';
import { ImpactService } from '../../services/impact.service';
import { FormatCo2eService } from 'src/app/shared/services/format-co2e.service';
import { ImpactChartControlsComponent } from '../impact-chart-controls/impact-chart-controls.component';
import { ImpactLegendCategoryComponent } from '../impact-legend-category/impact-legend-category.component';
import { ButtonModule } from 'primeng/button';
import { TooltipIconComponent } from 'src/app/shared/tooltip-icon/tooltip-icon.component';

@Component({
  selector: 'app-impact-overview',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SelectButtonModule,
    TooltipModule,
    LucideAngularModule,
    ChartDoughnutComponent,
    TranslocoModule,
    ImpactChartControlsComponent,
    ImpactLegendCategoryComponent,
    ButtonModule,
    TooltipIconComponent,
  ],
  templateUrl: './impact-overview.component.html',
  styles: [],
})
export class ImpactOverviewComponent {
  @Input() tripImpact: TripImpact | null = null;
  @Input() showPercentage: boolean = false;
  @Input() excludeMainTransport: boolean = false;
  @Input() groupTransports: boolean = false;
  @Input() tripName: string = '';
  @Input() tripId: string = '';
  @Input() travelers: number = 1;
  @Input() fullImpactDetails: {
    transport: Array<{
      type: string;
      impact: number;
      km: number;
      isMainTransport: boolean;
      title?: string;
    }>;
    accommodation: Array<{
      type: string;
      impact: number;
      title?: string;
      isEcoFriendly?: boolean;
    }>;
    activities: Array<{ type: string; impact: number; title?: string }>;
    food: Array<{ type: string; impact: number }>;
  } | null = null;
  @Input() company: ImpactReportCompany | null = null;
  @Input() clients: CompanyClient[] = [];

  @Output() showPercentageChange = new EventEmitter<boolean>();
  @Output() excludeMainTransportChange = new EventEmitter<boolean>();
  @Output() groupTransportsChange = new EventEmitter<boolean>();

  @ViewChild(ChartDoughnutComponent) chartComponent?: ChartDoughnutComponent;

  private router = inject(Router);
  private transloco = inject(TranslocoService);
  chartSize = inject(ChartSizeService);
  private impactDataService = inject(ImpactDataService);
  private currentUserService = inject(CurrentUserService);
  /** Owns `viewMode`, shared with the rest of the impact page. */
  protected impactService = inject(ImpactService);
  formatCo2e = inject(FormatCo2eService);
  isSuperAdmin = this.currentUserService.isSuperAdmin;
  readonly Math = Math;

  // État des sections collapsibles (toutes ouvertes par défaut)
  private categoryStateManager = new CategoryStateManager();

  // Icônes
  infoIcon = Info;
  chevronsUpIcon = ChevronsUp;
  chevronsDownIcon = ChevronsDown;

  // État global des sections (toutes ouvertes par défaut)
  allSectionsOpen = true;

  // Affichage du logo sur le graphique (position persistée en localStorage)
  private readonly LOGO_POSITION_STORAGE_KEY = 'impact.overview.logoPosition';
  logoPosition: ChartLogoPosition = this.loadLogoPosition();
  private logoSourceId: string | null = null;

  // Options pour SelectButton
  viewModeOptions = [
    {
      label: this.transloco.translate('impact.viewMode.group'),
      value: 'group',
    },
    {
      label: this.transloco.translate('impact.viewMode.individual'),
      value: 'individual',
    },
  ];

  onExcludeMainTransportChange(checked: boolean): void {
    this.excludeMainTransportChange.emit(checked);
  }

  onGroupTransportsChange(checked: boolean): void {
    this.groupTransportsChange.emit(checked);
  }

  // Sources de logo disponibles : company du trip + clients ayant un logo
  getLogoSources(): ChartLogoSource[] {
    return buildChartLogoSources(this.company, this.clients);
  }

  getSelectedLogoSourceId(): string | null {
    const sources = this.getLogoSources();
    if (sources.some((source) => source.id === this.logoSourceId)) {
      return this.logoSourceId;
    }
    return sources[0]?.id ?? null;
  }

  getSelectedLogoUrl(): string | null {
    const selectedId = this.getSelectedLogoSourceId();
    return (
      this.getLogoSources().find((source) => source.id === selectedId)
        ?.logoUrl ?? null
    );
  }

  isLogoCentered(): boolean {
    return this.logoPosition === 'center' && !!this.getSelectedLogoUrl();
  }

  onLogoPositionChange(position: ChartLogoPosition): void {
    this.logoPosition = position;
    localStorage.setItem(this.LOGO_POSITION_STORAGE_KEY, position);
  }

  private loadLogoPosition(): ChartLogoPosition {
    const saved = localStorage.getItem(this.LOGO_POSITION_STORAGE_KEY);
    const validPositions: ChartLogoPosition[] = ['none', 'center', 'side'];
    return validPositions.includes(saved as ChartLogoPosition)
      ? (saved as ChartLogoPosition)
      : 'center';
  }

  onLogoSourceChange(sourceId: string): void {
    this.logoSourceId = sourceId;
  }

  // Image introuvable/cassée : on retire le logo plutôt que d'afficher une icône cassée
  onLogoError(): void {
    this.logoPosition = 'none';
  }

  // Diamètre du logo centré, contenu dans le trou du donut
  getLogoDiameter(): number {
    const { size, thickness } = this.chartSize.getDimensions('impactOverview');
    const holeDiameter = size - 2 * thickness;
    return Math.round(holeDiameter * 0.85);
  }

  getCenterValue(): string {
    if (!this.tripImpact) return '0';

    if (this.showPercentage) {
      return '100%';
    }
    return this.formatCo2e.formatValue(this.getCurrentTotalImpact());
  }

  getCenterLabel(): string {
    if (this.showPercentage) {
      return 'Total';
    }
    return this.formatCo2e.getUnit(this.getCurrentTotalImpact(), {
      perPerson: this.impactService.perPerson(),
    });
  }

  private getCurrentTotalImpact(): number {
    if (!this.tripImpact) return 0;

    // Utiliser les bonnes valeurs selon le mode d'affichage
    let total = this.impactService.perPerson()
      ? this.tripImpact.impactPerTraveller
      : this.tripImpact.totalImpact;

    if (this.excludeMainTransport && this.fullImpactDetails) {
      // Calculer l'impact réel du transport principal depuis TOUTES les données
      const mainTransportImpact = this.fullImpactDetails.transport
        .filter((transport) => transport.isMainTransport)
        .reduce((sum, transport) => sum + transport.impact, 0);

      // Soustraire l'impact principal selon le mode
      if (this.impactService.perPerson()) {
        // En mode individuel, impactPerTraveller est déjà divisé par voyageur
        // donc on doit aussi diviser l'impact principal par voyageur
        total = total - mainTransportImpact / (this.travelers || 1);
      } else {
        total = total - mainTransportImpact;
      }
    }

    return Math.max(0, total);
  }

  /**
   * Get the impact value for a specific category, adjusted for view mode and exclusions
   */
  private getCategoryImpact(
    category: 'transport' | 'accommodation' | 'activity' | 'food' | 'waste',
  ): number {
    if (!this.tripImpact) return 0;

    // Dynamically construct the property key based on category and view mode
    const impactKey = this.impactService.perPerson()
      ? `${category}PerTraveller`
      : `${category}Impact`;

    let impact = this.tripImpact[impactKey as keyof TripImpact] as number;

    // Special case: transport with excludeMainTransport
    if (
      category === 'transport' &&
      this.excludeMainTransport &&
      this.fullImpactDetails
    ) {
      const mainTransportImpact = ImpactCalculator.calculateMainTransportImpact(
        this.fullImpactDetails.transport,
      );

      if (this.impactService.perPerson()) {
        impact -= mainTransportImpact / (this.travelers || 1);
      } else {
        impact -= mainTransportImpact;
      }
    }

    return Math.max(0, impact);
  }

  /**
   * Calculate percentage of a category relative to total impact
   */
  private getCategoryPercentage(
    category: 'transport' | 'accommodation' | 'activity' | 'food' | 'waste',
  ): number {
    const total = this.getCurrentTotalImpact();
    const impact = this.getCategoryImpact(category);
    return ImpactCalculator.calculatePercentage(impact, total);
  }

  // Public getters for template compatibility
  getTransportPercentage(): number {
    return this.getCategoryPercentage('transport');
  }

  getAccommodationPercentage(): number {
    return this.getCategoryPercentage('accommodation');
  }

  getActivityPercentage(): number {
    return this.getCategoryPercentage('activity');
  }

  getFoodPercentage(): number {
    return this.getCategoryPercentage('food');
  }

  getWastePercentage(): number {
    return this.getCategoryPercentage('waste');
  }

  getIntensityPercentage(): number {
    if (!this.tripImpact) return 0;
    // Échelle arbitraire: 0-50 kg CO2e/jour/voyageur = 0-100%
    return Math.min((this.tripImpact.carbonIntensity / 50) * 100, 100);
  }

  getIntensityColorClass(): string {
    const intensity = this.tripImpact?.carbonIntensity || 0;
    if (intensity < 10) return 'bg-validation-500';
    if (intensity < 25) return 'bg-warning-500';
    return 'bg-error-500';
  }

  getIntensityDescription(): string {
    const intensity = this.tripImpact?.carbonIntensity || 0;
    if (intensity < 10) return 'Faible intensité';
    if (intensity < 25) return 'Intensité modérée';
    return 'Forte intensité';
  }

  getBudgetColorClass(): string {
    const percentage = this.tripImpact?.percentageOfAnnualBudget || 0;
    if (percentage < 25) return 'bg-validation-500';
    if (percentage < 75) return 'bg-warning-500';
    return 'bg-error-500';
  }

  /**
   * Build configuration for ImpactDataService
   */
  private buildDataConfig() {
    if (!this.tripImpact || !this.fullImpactDetails) return null;

    return {
      tripImpact: this.tripImpact,
      fullImpactDetails: this.fullImpactDetails,
      viewMode: this.impactService.viewMode(),
      travelers: this.travelers,
      excludeMainTransport: this.excludeMainTransport,
      groupTransports: this.groupTransports,
      showPercentage: this.showPercentage,
      categoryStateManager: this.categoryStateManager,
    };
  }

  // Données pour le graphique Chart.js
  getChartData(): ChartData[] {
    const config = this.buildDataConfig();
    if (!config) return [];
    return this.impactDataService.generateChartData(config);
  }

  // Génère les données pour la légende enrichie organisée par catégorie
  getLegendData(): LegendCategory[] {
    const config = this.buildDataConfig();
    if (!config) return [];
    return this.impactDataService.generateLegendData(config);
  }

  // Méthode pour toggler l'état d'expansion d'une catégorie
  toggleCategory(category: LegendCategory): void {
    category.isExpanded = this.categoryStateManager.toggle(
      category.translationKey,
    );
    // Update global state based on individual categories
    this.updateGlobalSectionsState();
  }

  // Méthode pour ouvrir/fermer toutes les sections
  toggleAllSections(): void {
    this.allSectionsOpen = !this.allSectionsOpen;
    const legendData = this.getLegendData();
    const categoryKeys = legendData.map((cat) => cat.translationKey);

    if (this.allSectionsOpen) {
      this.categoryStateManager.expandAll(categoryKeys);
      legendData.forEach((cat) => (cat.isExpanded = true));
    } else {
      this.categoryStateManager.collapseAll(categoryKeys);
      legendData.forEach((cat) => (cat.isExpanded = false));
    }
  }

  // Update the global state based on individual category states
  private updateGlobalSectionsState(): void {
    const legendData = this.getLegendData();
    const categoryKeys = legendData.map((cat) => cat.translationKey);
    this.allSectionsOpen =
      this.categoryStateManager.areAllExpanded(categoryKeys);
  }

  // Methods to handle legend hover interactions
  onLegendItemHover(
    item: { type: string; label: string; title?: string },
    indexInCategory: number,
    categoryIndex: number,
  ): void {
    if (!this.chartComponent) return;

    const chartData = this.getChartData();
    const legendData = this.getLegendData();

    // Calculate global offset: total number of elements in all previous categories
    let globalOffset = 0;
    for (let i = 0; i < categoryIndex; i++) {
      globalOffset += legendData[i].items.length;
    }

    // The global index in chartData
    const globalIndex = globalOffset + indexInCategory;

    if (globalIndex >= 0 && globalIndex < chartData.length) {
      this.chartComponent.highlightSegment(globalIndex);
    }
    // Silently ignore out of bounds indices
  }

  // Hover on category header: highlight all segments of this category
  onCategoryHeaderHover(category: LegendCategory, categoryIndex: number): void {
    if (!this.chartComponent || !this.chartComponent.chart) return;

    const legendData = this.getLegendData();

    // In grouped mode (itemsAreDetailsOnly), the entire category = 1 single segment
    if (category.itemsAreDetailsOnly) {
      // In grouped mode, segment index = categoryIndex
      this.chartComponent.chart.setActiveElements([
        {
          datasetIndex: 0,
          index: categoryIndex,
        },
      ]);
      this.chartComponent.chart.update('none');
      return;
    }

    // In normal mode, calculate global offset for all items in the category
    let globalOffset = 0;
    for (let i = 0; i < categoryIndex; i++) {
      globalOffset += legendData[i].items.length;
    }

    // Create an array of all indices of elements in this category
    const activeElements = category.items.map((_, itemIndex) => ({
      datasetIndex: 0,
      index: globalOffset + itemIndex,
    }));

    // Highlight all segments of the category
    this.chartComponent.chart.setActiveElements(activeElements);
    this.chartComponent.chart.update('none');
  }

  onLegendItemLeave(): void {
    if (this.chartComponent) {
      this.chartComponent.clearHighlight();
    }
  }

  onEditTrip() {
    if (this.tripId) {
      this.router.navigate([`/home/trips/${this.tripId}/dashboard`]);
    }
  }

  onContribute() {
    if (this.tripId) {
      this.router.navigate(['/home/trips', this.tripId, 'contribute']);
    }
  }

  // Get badges for a specific category from topImpactElements
  // getBadges(
  //   category: 'transport' | 'accommodation' | 'food' | 'activities',
  // ): string[] {
  //   if (!this.topImpactElements) return [];
  //   return this.topImpactElements[category]
  //     .slice(0, 3)
  //     .map((el) => translateElementType(this.transloco, el.type));
  // }

  getDisplayUnit(): string {
    return this.formatCo2e.getUnit(this.getCurrentTotalImpact(), {
      perPerson: this.impactService.perPerson(),
    });
  }

  formatImpactValue(impact: number): string {
    return this.formatCo2e.formatValue(impact);
  }

  getUnitForImpact(impact: number): string {
    return this.formatCo2e.getUnit(impact, {
      perPerson: this.impactService.perPerson(),
    });
  }

  getBaseUnit(): string {
    return this.formatCo2e.getUnit(this.getCurrentTotalImpact());
  }
}
